import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import PeopleIcon from "@material-ui/icons/People";
import DownloadIcon from "@material-ui/icons/GetApp";
import MoreVertIcon from "@material-ui/icons/MoreVert";

import MainContainer from "../../components/MainContainer";
import { PageHeader, StatusBadge } from "../../components/ui";
import EmptyState from "../../components/EmptyState";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListDialog from "../../components/ContactListDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";

import planilhaExemplo from "../../assets/planilha.xlsx";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getTokens } from "../../styles/tokens";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTLISTS") {
    const contactLists = action.payload;
    const newContactLists = [];

    contactLists.forEach((contactList) => {
      const contactListIndex = state.findIndex((u) => u.id === contactList.id);
      if (contactListIndex !== -1) {
        state[contactListIndex] = contactList;
      } else {
        newContactLists.push(contactList);
      }
    });

    return [...state, ...newContactLists];
  }

  if (action.type === "UPDATE_CONTACTLIST") {
    const contactList = action.payload;
    const contactListIndex = state.findIndex((u) => u.id === contactList.id);

    if (contactListIndex !== -1) {
      state[contactListIndex] = contactList;
      return [...state];
    } else {
      return [contactList, ...state];
    }
  }

  if (action.type === "DELETE_CONTACTLIST") {
    const contactListId = action.payload;

    const contactListIndex = state.findIndex((u) => u.id === contactListId);
    if (contactListIndex !== -1) {
      state.splice(contactListIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    filters: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 12,
      marginBottom: 16,
    },
    search: {
      width: "min(320px, 100%)",
      [theme.breakpoints.down("xs")]: { width: "100%" },
    },
    searchIcon: { color: t.textMuted },
    mainPaper: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxShadow: t.shadow.card,
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      overflow: "auto",
      ...theme.scrollbarStyles,
    },
    table: { minWidth: 560 },
    nameCell: { fontWeight: 600, color: t.text },
    countCell: { width: 140 },
    actionsCell: { width: 150, whiteSpace: "nowrap" },
    menuIcon: { minWidth: 32, color: "inherit" },
    deleteItem: { color: t.semantic.error },
  };
});

const ContactLists = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedContactList, setSelectedContactList] = useState(null);
  const [deletingContactList, setDeletingContactList] = useState(null);
  const [contactListModalOpen, setContactListModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [contactLists, dispatch] = useReducer(reducer, []);
  // menu "mais ações" da linha: âncora + lista a que se refere
  const [rowMenu, setRowMenu] = useState({ anchorEl: null, contactList: null });
  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);


  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContactLists = async () => {
        try {
          const { data } = await api.get("/contact-lists/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_CONTACTLISTS", payload: data.records });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContactLists();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    // const socket = socketManager.GetSocket();

    const onContactListEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTLIST", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACTLIST", payload: +data.id });
      }
    };

    socket.on(`company-${companyId}-ContactList`, onContactListEvent);

    return () => {
      socket.off(`company-${companyId}-ContactList`, onContactListEvent);
    };
  }, []);

  const handleOpenContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(true);
  };

  const handleCloseContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditContactList = (contactList) => {
    setSelectedContactList(contactList);
    setContactListModalOpen(true);
  };

  const handleDeleteContactList = async (contactListId) => {
    try {
      await api.delete(`/contact-lists/${contactListId}`);
      toast.success(i18n.t("contactLists.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContactList(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContacts = (id) => {
    history.push(`/contact-lists/${id}/contacts`);
  };

  const closeRowMenu = () => {
    setRowMenu((prev) => ({ ...prev, anchorEl: null }));
  };

  return (
    <MainContainer maxWidth>
      <ConfirmationModal
        title={
          deletingContactList &&
          `${i18n.t("contactLists.confirmationModal.deleteTitle")} ${deletingContactList.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteContactList(deletingContactList.id)}
      >
        {i18n.t("contactLists.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ContactListDialog
        open={contactListModalOpen}
        onClose={handleCloseContactListModal}
        aria-labelledby="form-dialog-title"
        contactListId={selectedContactList && selectedContactList.id}
      />
      <PageHeader
        title={i18n.t("contactLists.title")}
        subtitle="Públicos usados nos disparos de campanha."
        badge={
          <StatusBadge
            color="neutral"
            dot={false}
            label={String(contactLists.length)}
          />
        }
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenContactListModal}
          >
            {i18n.t("contactLists.buttons.add")}
          </Button>
        }
      />
      <div className={classes.filters}>
        <TextField
          className={classes.search}
          variant="outlined"
          size="small"
          placeholder={i18n.t("contacts.searchPlaceholder")}
          type="search"
          value={searchParam}
          onChange={handleSearch}
          inputProps={{ "aria-label": i18n.t("contacts.searchPlaceholder") }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" className={classes.searchIcon} />
              </InputAdornment>
            ),
          }}
        />
      </div>
      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.tableScroll} onScroll={handleScroll}>
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("contactLists.table.name")}</TableCell>
                <TableCell align="right" className={classes.countCell}>
                  {i18n.t("contactLists.table.contacts")}
                </TableCell>
                <TableCell align="right" className={classes.actionsCell}>
                  {i18n.t("contactLists.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {contactLists.map((contactList) => (
                  <TableRow key={contactList.id}>
                    <TableCell className={classes.nameCell}>
                      {contactList.name}
                    </TableCell>
                    <TableCell align="right" className={classes.countCell}>
                      {contactList.contactsCount || 0}
                    </TableCell>
                    <TableCell align="right" className={classes.actionsCell}>
                      <Tooltip title="Ver contatos da lista">
                        <IconButton
                          size="small"
                          aria-label={`Ver contatos da lista ${contactList.name}`}
                          onClick={() => goToContacts(contactList.id)}
                        >
                          <PeopleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={i18n.t("contactLists.dialog.edit")}>
                        <IconButton
                          size="small"
                          aria-label={`${i18n.t("contactLists.dialog.edit")} ${contactList.name}`}
                          onClick={() => handleEditContactList(contactList)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Mais ações">
                        <IconButton
                          size="small"
                          aria-label={`Mais ações de ${contactList.name}`}
                          aria-haspopup="true"
                          onClick={(e) =>
                            setRowMenu({ anchorEl: e.currentTarget, contactList })
                          }
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && contactLists.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} style={{ borderBottom: "none" }}>
                      <EmptyState
                        icon={<PeopleIcon />}
                        title="Nenhuma lista de contatos encontrada"
                        message={
                          searchParam
                            ? "Nenhuma lista corresponde à busca."
                            : "Crie uma lista e importe os contatos para usar nas campanhas."
                        }
                        action={
                          !searchParam && (
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<AddIcon />}
                              onClick={handleOpenContactListModal}
                            >
                              {i18n.t("contactLists.buttons.add")}
                            </Button>
                          )
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
                {loading && <TableRowSkeleton columns={3} />}
              </>
            </TableBody>
          </Table>
        </div>
      </Paper>
      <Menu
        anchorEl={rowMenu.anchorEl}
        open={Boolean(rowMenu.anchorEl)}
        onClose={closeRowMenu}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          component="a"
          href={planilhaExemplo}
          download="planilha.xlsx"
          onClick={closeRowMenu}
        >
          <ListItemIcon className={classes.menuIcon}>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          Baixar planilha exemplo
        </MenuItem>
        <MenuItem
          className={classes.deleteItem}
          onClick={() => {
            closeRowMenu();
            setConfirmModalOpen(true);
            setDeletingContactList(rowMenu.contactList);
          }}
        >
          <ListItemIcon className={classes.menuIcon}>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          {i18n.t("contactLists.confirmationModal.deleteTitle")}
        </MenuItem>
      </Menu>
    </MainContainer>
  );
};

export default ContactLists;
